import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { resolveAssetUrl } from '../../../api';
import DashboardLayout from '../../../Components/Dashboard/DashboardLayout';
import './AdminSuite.css';

const formatDate = value => {
  if (!value) return 'TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString();
};

const formatChoice = (value, dictionary) => {
  if (!value) return '-';
  return dictionary[value] || value;
};

const levelOfStudiesLabels = {
  bachelors_degree: "Bachelor's degree",
  masters_degree: "Master's Degree",
  university_student_last_level: 'University Student at the last level',
};

const employmentStatusLabels = {
  employed: 'Employed',
  unemployed: 'Unemployed',
  student: 'Student',
  business_owner: 'Business Owner',
};

const trainingTimeLabels = {
  day_program: 'Day Program',
  weekend_program: 'Weekend Program',
  evening_program: 'Evening Program',
  online_program: 'Online Program',
  day_afternoon_session: 'Day Afternoon Session',
};

const skillLevelLabels = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const paymentMethodLabels = {
  momo_pay: 'MOMO PAY (1708263)',
  bank_transfer: 'BK Account Transfer',
};

const packageTypeLabels = {
  full_package: 'Full package',
  individual_modules: 'Individual modules',
};

const paymentPlanLabels = {
  full: 'Full payment',
  installment: 'Installment plan',
};

const enrollmentStatusLabels = {
  pending_payment_verification: 'Waiting for approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const enrollmentStatusBadgeClasses = {
  pending_payment_verification: 'bg-warning-subtle text-warning-emphasis border border-warning-subtle',
  approved: 'bg-success-subtle text-success-emphasis border border-success-subtle',
  rejected: 'bg-danger-subtle text-danger-emphasis border border-danger-subtle',
};

const AdminEnrollmentsPage = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [totalEnrollments, setTotalEnrollments] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [reviewNotes, setReviewNotes] = useState({});

  useEffect(() => {
    load();
  }, [statusFilter, courseFilter, intakeFilter, currentPage]);

  useEffect(() => {
    loadCourses();
  }, []);

  const load = async () => {
    const res = await api.get('/admin/enrollments', {
      params: {
        ...(statusFilter === 'all' ? {} : { status: statusFilter }),
        ...(courseFilter === 'all' ? {} : { course_id: courseFilter }),
        ...(intakeFilter === 'all' ? {} : { course_intake_id: intakeFilter }),
        page: currentPage,
      },
    });

    setEnrollments(res.data?.data || []);
    setTotalEnrollments(res.data?.total || 0);
    setCurrentPage(res.data?.current_page || 1);
    setLastPage(res.data?.last_page || 1);
  };

  const loadCourses = async () => {
    const res = await api.get('/admin/courses');
    setCourses(res.data?.data || []);
  };

  const updateReviewNotes = (id, value) => {
    setReviewNotes(current => ({
      ...current,
      [id]: value,
    }));
  };

  const review = async (id, decision, notes) => {
    if (decision === 'rejected' && !notes.trim()) {
      toast.error('Add a rejection comment before rejecting the enrollment.');
      return;
    }

    await api.patch(`/admin/enrollments/${id}/review`, {
      status: decision,
      review_notes: notes,
    });

    toast.success('Reviewed');
    setReviewNotes(current => ({
      ...current,
      [id]: '',
    }));
    load();
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setCourseFilter('all');
    setIntakeFilter('all');
    setCurrentPage(1);
  };

  const availableIntakes = courses
    .filter(course => courseFilter === 'all' || String(course.id) === String(courseFilter))
    .flatMap(course => (course.intakes || []).map(intake => ({
      id: intake.id,
      name: intake.name,
      courseTitle: course.title,
    })));

  return (
    <DashboardLayout>
      <div className="container py-4 admin-suite">
        <div className="admin-enrollments-sticky-top">
          <div className="admin-suite-header admin-suite-header-title-row">
            <div>
              <h3 className="admin-suite-title">Enrollment Management</h3>
              <p className="admin-suite-subtitle">Review payment proofs and approve student intakes.</p>
            </div>
          </div>

          <div className="admin-suite-header admin-suite-header-filter-row">
            <div className="admin-enrollments-filter-label small text-muted">Filters</div>
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 220 }}
                value={courseFilter}
                onChange={event => {
                  setCurrentPage(1);
                  setCourseFilter(event.target.value);
                  setIntakeFilter('all');
                }}
              >
                <option value="all">All courses</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select-sm"
                style={{ minWidth: 220 }}
                value={intakeFilter}
                onChange={event => {
                  setCurrentPage(1);
                  setIntakeFilter(event.target.value);
                }}
              >
                <option value="all">All intakes</option>
                {availableIntakes.map(intake => (
                  <option key={intake.id} value={intake.id}>
                    {intake.courseTitle} - {intake.name}
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select-sm"
                style={{ minWidth: 220 }}
                value={statusFilter}
                onChange={event => {
                  setCurrentPage(1);
                  setStatusFilter(event.target.value);
                }}
              >
                <option value="all">All statuses</option>
                <option value="pending_payment_verification">Waiting for approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>

              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                Clear filters
              </button>
              <span className="badge bg-light text-dark">Total: {totalEnrollments}</span>
            </div>
          </div>
        </div>

        <div className="admin-enrollments-scrollable list-group mt-3 shadow-sm rounded-3 admin-card">
          {enrollments.map(enrollment => {
            const latestPayment = enrollment.payments?.[0];
            const proofUrl = resolveAssetUrl(enrollment.payment_proof || latestPayment?.proof_path);
            const currentReviewNote = reviewNotes[enrollment.id] ?? latestPayment?.review_notes ?? '';
            const statusLabel = enrollmentStatusLabels[enrollment.status] || enrollment.status;
            const statusBadgeClass = enrollmentStatusBadgeClasses[enrollment.status] || 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle';

            return (
              <div key={enrollment.id} className="list-group-item p-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="mb-1">
                      <strong>
                        {enrollment.applicant_name || enrollment.user?.name || enrollment.user?.fullName}
                      </strong>
                      {' '}-{' '}
                      {enrollment.course?.title}
                    </div>
                    <div className="small text-muted d-flex align-items-center gap-2 flex-wrap">
                      <span>Status:</span>
                      <span className={`badge rounded-pill ${statusBadgeClass}`}>{statusLabel}</span>
                    </div>
                    <div className="small text-muted">
                      Email: {enrollment.applicant_email || enrollment.user?.email || '-'}
                    </div>
                    <div className="small text-muted">
                      Intake: {enrollment.intakeSession?.name || enrollment.intake || '-'}
                      {enrollment.intakeSession
                        ? ` (${formatDate(enrollment.intakeSession.start_date)} - ${formatDate(enrollment.intakeSession.end_date)})`
                        : ''}
                    </div>
                    <div className="small text-muted">Phone: {enrollment.phone_number}</div>
                    <div className="small text-muted">
                      Level of studies: {formatChoice(enrollment.level_of_studies, levelOfStudiesLabels)}
                    </div>
                    <div className="small text-muted">
                      Employment status: {formatChoice(enrollment.employment_status, employmentStatusLabels)}
                    </div>
                    <div className="small text-muted">
                      Training time: {formatChoice(enrollment.favorable_training_time, trainingTimeLabels)}
                    </div>
                    <div className="small text-muted">
                      Accounting level: {formatChoice(enrollment.accounting_skill_level, skillLevelLabels)}
                    </div>
                    <div className="small text-muted">
                      Excel level: {formatChoice(enrollment.excel_skill_level, skillLevelLabels)}
                    </div>
                    <div className="small text-muted">
                      Payment method: {formatChoice(enrollment.payment_method, paymentMethodLabels)}
                    </div>
                    <div className="small text-muted">
                      Package: {formatChoice(enrollment.package_type, packageTypeLabels)}
                    </div>
                    <div className="small text-muted">
                      Payment plan: {formatChoice(enrollment.payment_plan, paymentPlanLabels)}
                    </div>
                    <div className="small text-muted">
                      Estimated fee: {enrollment.course?.fee_currency || 'RWF'}{' '}
                      {Number(enrollment.quoted_amount || 0).toLocaleString()}
                    </div>
                    {enrollment.payment_reference && (
                      <div className="small text-muted">Payment reference: {enrollment.payment_reference}</div>
                    )}
                    {Array.isArray(enrollment.selectedModules) && enrollment.selectedModules.length > 0 && (
                      <div className="small text-muted">
                        Modules: {enrollment.selectedModules.map(module => module.name).join(', ')}
                      </div>
                    )}
                    <div className="small text-muted">Notes: {enrollment.additional_notes || '-'}</div>
                    {latestPayment?.review_notes && enrollment.status !== 'pending_payment_verification' && (
                      <div className="small mt-2">
                        <strong>Review comment:</strong> {latestPayment.review_notes}
                      </div>
                    )}
                    {proofUrl && (
                      <a
                        className="btn btn-sm btn-primary mt-2"
                        href={proofUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Payment Proof
                      </a>
                    )}
                    {!proofUrl && (
                      <div className="small text-danger mt-2">Payment proof document not available.</div>
                    )}
                  </div>

                  {enrollment.status === 'pending_payment_verification' && (
                    <div className="d-flex flex-column gap-2" style={{ minWidth: 260 }}>
                      <label className="form-label small fw-semibold mb-0">
                        Reason for rejection
                      </label>
                      <textarea
                        className="form-control form-control-sm"
                        rows="3"
                        placeholder="Required if you reject this enrollment"
                        value={currentReviewNote}
                        onChange={event => updateReviewNotes(enrollment.id, event.target.value)}
                      />
                      <div className="small text-muted">You can also use this field for an optional approval note.</div>
                      <button className="btn btn-sm btn-success" onClick={() => review(enrollment.id, 'approved', currentReviewNote)}>
                        Approve
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => review(enrollment.id, 'rejected', currentReviewNote)}>
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {enrollments.length === 0 && (
            <div className="list-group-item p-4 text-center text-muted">
              No enrollments match the selected filters.
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-between gap-3 mt-3 flex-wrap">
          <div className="small text-muted">
            Page {currentPage} of {lastPage}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage(page => Math.min(lastPage, page + 1))}
              disabled={currentPage >= lastPage}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminEnrollmentsPage;
